import type { WidgetBubbleShape, WidgetSettings } from '@livechat/shared';

const interFont = '"Manorsoft Inter", Inter, ui-sans-serif, system-ui, sans-serif';
const systemFont =
	'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

function channel(value: number): number {
	const normalised = value / 255;
	return normalised <= 0.04045 ? normalised / 12.92 : Math.pow((normalised + 0.055) / 1.055, 2.4);
}

function luminance(hex: string): number {
	return (
		0.2126 * channel(Number.parseInt(hex.slice(1, 3), 16)) +
		0.7152 * channel(Number.parseInt(hex.slice(3, 5), 16)) +
		0.0722 * channel(Number.parseInt(hex.slice(5, 7), 16))
	);
}

export function contrastRatio(foreground: string, background: string): number {
	const lighter = Math.max(luminance(foreground), luminance(background));
	const darker = Math.min(luminance(foreground), luminance(background));
	return (lighter + 0.05) / (darker + 0.05);
}

function bubbleRadius(shape: WidgetBubbleShape, radius: number, visitor: boolean): string {
	if (shape === 'speech') {
		return visitor
			? `${radius}px ${Math.min(6, radius)}px ${radius}px ${radius}px`
			: `${Math.min(6, radius)}px ${radius}px ${radius}px ${radius}px`;
	}
	return `${radius}px`;
}

export function widgetAppearanceVariables(settings: WidgetSettings): Record<string, string> {
	const { appearance } = settings;
	return {
		'--widget-accent': settings.accentColour,
		'--widget-agent-background': appearance.agentBubble.backgroundColour,
		'--widget-agent-font-size': `${appearance.visitorBubble.fontSize}px`,
		'--widget-agent-radius': bubbleRadius(
			appearance.visitorBubble.shape,
			appearance.visitorBubble.radius,
			false,
		),
		'--widget-agent-text': appearance.agentBubble.textColour,
		'--widget-launcher-background': appearance.launcher.backgroundColour,
		'--widget-launcher-border': '0 none transparent',
		'--widget-launcher-font': systemFont,
		'--widget-launcher-icon': appearance.launcher.iconColour,
		'--widget-launcher-radius': `${appearance.launcher.radius}px`,
		'--widget-panel-background': appearance.panel.backgroundColour,
		'--widget-panel-border': '0 none transparent',
		'--widget-panel-font': interFont,
		'--widget-panel-radius': `${appearance.panel.radius}px`,
		'--widget-panel-surface': appearance.panel.surfaceColour,
		'--widget-panel-text': appearance.panel.textColour,
		'--widget-visitor-background': appearance.visitorBubble.backgroundColour,
		'--widget-visitor-font-size': `${appearance.visitorBubble.fontSize}px`,
		'--widget-visitor-radius': bubbleRadius(
			appearance.visitorBubble.shape,
			appearance.visitorBubble.radius,
			true,
		),
		'--widget-visitor-text': appearance.visitorBubble.textColour,
	};
}
